// import components
import { Tooltip, Typography } from '@mui/material';
import { Icon } from '@sdb/planning-ui';

// import external libraries
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

// import constants
import {
  ALL_TILE_ICON,
  BACKLOG_CATEGORIES,
  IMPLEMENTED_CATEGORIES
} from '../../Backlog.consts';

// import styles
import {
  TileButton,
  TileCount,
  TileIcon,
  TileLabel,
  TileSeparator,
  TilesContainer,
  TileValueRow
} from './BacklogCategoryTiles.styles';

// import types
import type { BacklogCategoryTilesProps } from './BacklogCategoryTiles.types';

const BacklogCategoryTiles = ({
  categoryCounts,
  activeCategories,
  onToggleCategory,
  onToggleAll
}: BacklogCategoryTilesProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'backlog' });

  const allActive = IMPLEMENTED_CATEGORIES.every((category) =>
    activeCategories.includes(category)
  );
  const totalCount = IMPLEMENTED_CATEGORIES.reduce(
    (total, category) => total + (categoryCounts[category] ?? 0),
    0
  );

  // The tiles read alphabetically by label — but the working (implemented) ones first, then the ones
  // still in development, each group alphabetical, so the enabled tiles cluster to the left.
  const orderedCategories = [...BACKLOG_CATEGORIES].sort((first, second) => {
    if (first.isImplemented !== second.isImplemented) {
      return first.isImplemented ? -1 : 1;
    }
    return t(`categories.${first.category}`).localeCompare(
      t(`categories.${second.category}`)
    );
  });

  return (
    <TilesContainer>
      <TileButton
        onClick={onToggleAll}
        isActive={allActive}
        aria-pressed={allActive}
        // aria-label replaces the accessible name, so the count has to be part of it or a screen
        // reader announces the tile with no number at all.
        aria-label={`${t('categories.all')}: ${totalCount}`}
      >
        <TileLabel noWrap>{t('categories.all')}</TileLabel>
        <TileValueRow>
          <TileIcon isActive={allActive} isAccent>
            <Icon type={ALL_TILE_ICON} />
          </TileIcon>
          <TileCount isActive={allActive}>{totalCount}</TileCount>
        </TileValueRow>
      </TileButton>

      <TileSeparator />

      {orderedCategories.map(({ category, iconType, isImplemented }) => {
        const isActive = isImplemented && activeCategories.includes(category);
        const label = t(`categories.${category}`);
        const tile = (
          <TileButton
            key={category}
            onClick={() => onToggleCategory(category)}
            disabled={!isImplemented}
            isActive={isActive}
            aria-pressed={isActive}
            // The count (or the in-progress state for a not-yet-backed category) has to live in the
            // aria-label, since it replaces rather than adds to the accessible name.
            aria-label={`${label}: ${
              isImplemented
                ? categoryCounts[category] ?? 0
                : t('categories.inProgress')
            }`}
          >
            <TileLabel noWrap>{label}</TileLabel>
            <TileValueRow>
              <TileIcon isActive={isActive}>
                <Icon type={iconType} />
              </TileIcon>
              {/* No number for a category with nothing behind it — inventing one would mislead. */}
              <TileCount isActive={isActive}>
                {isImplemented ? categoryCounts[category] ?? 0 : '–'}
              </TileCount>
            </TileValueRow>
          </TileButton>
        );

        if (isImplemented) return tile;

        return (
          <Tooltip key={category} title={t('categories.inProgress')} arrow>
            {/* A disabled button fires no events, so the tooltip needs a wrapper to hang on. */}
            <Typography component="span" display="inline-flex">
              {tile}
            </Typography>
          </Tooltip>
        );
      })}
    </TilesContainer>
  );
};

export default memo(BacklogCategoryTiles);
